import React from "react";
import PropTypes from "prop-types";
import { Trash2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import config from "../../../config";
import { RentNowButton } from "../homePageComponents/RentNowButton";
import { AddToWishlistButton } from "../homePageComponents/ButtonComponents";
import RatingStars from "../homePageComponents/RatingStars";
import ReminderManager from "../../../services/home/ReminderManager";

export default function CartItem({
  book,
  isSelected,
  onSelect,
  onRemove,
}) {
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);

  const isOutOfStock = (book.stock || "").toLowerCase() === "out_of_stock";

  const handleReminder = async () => {
    if (!book?.id) return;
    try {
      if (!userId) {
        toast.error("Please login to set a reminder.");
        return;
      }
      await ReminderManager.createReminder(userId, book.id);
      toast.success("Reminder set successfully!");
    } catch (error) {
      console.error("Failed to set reminder:", error);
      toast.error("Failed to set reminder.");
    }
  };

  const coverImg = Array.isArray(book.imageUrl)
    ? book.imageUrl[0]
    : book.imageUrl;

  const resolvedImageUrl =
    coverImg && coverImg !== "defaultImage.jpg" && coverImg !== "/default-cover.jpg"
      ? coverImg.startsWith("http")
        ? coverImg
        : `${config.url}${coverImg}`
      : "/images/defaultBook.png";

  const renderStockBadge = () => {
    const stockStr = (book.stock || "").toLowerCase();
    if (stockStr === "in_stock") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          In Stock
        </span>
      );
    }
    if (stockStr === "limited_stock") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Limited Stock
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
        Out of Stock
      </span>
    );
  };

  return (
    <div
      className={`bg-white rounded-xl border transition-all duration-200 p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between ${
        isSelected
          ? "border-orange-300 ring-1 ring-orange-200 shadow-sm"
          : "border-gray-200/80 hover:border-orange-200 shadow-sm hover:shadow"
      } ${isOutOfStock ? "opacity-80 bg-gray-50/50" : ""}`}
    >
      {/* Left: Checkbox + Book Cover + Details */}
      <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0 w-full sm:w-auto">
        {/* Selection Checkbox with white checkmark */}
        <div className="pt-2 sm:pt-3 flex-shrink-0">
          <label
            htmlFor={`select-book-${book.id}`}
            className={`relative flex items-center justify-center cursor-pointer select-none ${
              isOutOfStock ? "cursor-not-allowed opacity-40" : ""
            }`}
            title={
              isOutOfStock
                ? "Out of stock books cannot be selected for rent"
                : "Select book for rent"
            }
          >
            <input
              type="checkbox"
              id={`select-book-${book.id}`}
              checked={isSelected}
              onChange={onSelect}
              disabled={isOutOfStock}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all duration-150 ${
                isSelected
                  ? "bg-primary border-primary shadow-sm"
                  : "bg-white border-gray-300 hover:border-primary"
              }`}
            >
              {isSelected && (
                <Check size={13} strokeWidth={3} className="text-white" />
              )}
            </div>
          </label>
        </div>


        {/* Cover Image */}
        <div
          onClick={() => navigate(`/book/${book.id}`)}
          className="cursor-pointer flex-shrink-0 group relative overflow-hidden rounded-lg border border-gray-100 shadow-sm bg-gray-50"
        >
          <img
            src={resolvedImageUrl}
            alt={book.title}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/images/defaultBook.png";
            }}
            className="w-20 h-28 sm:w-24 sm:h-32 object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* Book Info */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <h2
            className="font-semibold text-gray-900 hover:text-primary transition-colors cursor-pointer text-base sm:text-lg line-clamp-2 leading-snug"
            onClick={() => navigate(`/book/${book.id}`)}
            title={book.title}
          >
            {book.title.includes("[") ? (
              <>
                <span>{book.title.split("[")[0].trim()}</span>
                <span className="block text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                  [{book.title.split("[")[1]?.replace("]", "").trim()}]
                </span>
              </>
            ) : (
              book.title
            )}
          </h2>

          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-gray-600">
            {book.author && (
              <span>
                <span className="text-gray-400">By</span>{" "}
                <span className="font-medium text-gray-700">{book.author}</span>
              </span>
            )}
            {book.publisher && book.publisher !== "Unknown Publisher" && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-gray-500">{book.publisher}</span>
              </>
            )}
          </div>

          {book.isbn && book.isbn !== "N/A" && (
            <p className="text-xs text-gray-400">ISBN: {book.isbn}</p>
          )}

          {book.rating ? (
            <div className="flex items-center gap-1.5 pt-0.5">
              <RatingStars rating={book.rating} size={13} />
              <span className="text-xs font-semibold text-gray-700">
                {Number(book.rating).toFixed(1)}
              </span>
            </div>
          ) : null}

          <div className="pt-1">{renderStockBadge()}</div>
        </div>
      </div>

      {/* Right: Actions Cluster (Wishlist + Trash at top, Rent/Remind at bottom) */}
      <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-between w-full sm:w-auto self-stretch sm:self-auto pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100 flex-shrink-0 gap-3">
        {/* Wishlist & Delete action icons */}
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-full border border-gray-200 bg-gray-50/80 hover:bg-orange-50 hover:border-orange-200 flex items-center justify-center transition-colors"
            title="Move to Wishlist"
          >
            <AddToWishlistButton book={{ id: book.id }} size={16} />
          </div>

          <button
            type="button"
            onClick={() => onRemove(book.id)}
            className="w-8 h-8 rounded-full border border-gray-200 bg-gray-50/80 hover:bg-red-50 hover:border-red-200 text-gray-400 hover:text-red-500 flex items-center justify-center transition-colors"
            title="Remove from Cart"
            aria-label="Remove item"
          >
            <Trash2 size={16} />
          </button>
        </div>

        {/* Rent Now / Remind Me button */}
        <div className="w-28">
          {["in_stock", "limited_stock"].includes(
            (book.stock || "").toLowerCase()
          ) ? (
            <RentNowButton
              book={{ id: book.id, inStock: book.stock }}
              className="w-full text-xs py-1.5 px-3 rounded-lg shadow-sm font-medium"
            />
          ) : (
            <button
              type="button"
              onClick={handleReminder}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-semibold py-1.5 px-3 rounded-lg shadow-sm transition"
            >
              Remind Me
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

CartItem.propTypes = {
  book: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    author: PropTypes.string,
    publisher: PropTypes.string,
    isbn: PropTypes.string,
    imageUrl: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    rating: PropTypes.number,
    stock: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
